import { useState } from "react";
import { motion, useTransform } from "motion/react";
import { appliancePulse } from "../lib/appliance-pulse";
import { BiSolidWasher, BiSolidDryer, BiSolidDish } from "react-icons/bi";
import { api, ApiError, errMsg } from "../lib/api";
import { APPLIANCE_LABEL } from "../lib/types";
import type { Appliance, ApplianceState, Booking } from "../lib/types";
import { Button, Pill } from "./ui";
import { Avatar } from "./Avatar";
import { formatRelativeTime, formatDateTime } from "../lib/format";

const STATE_LABEL: Record<ApplianceState, string> = {
  IDLE: "Idle",
  RUNNING: "Running",
  DONE: "Done",
};

const APPLIANCE_ICON = {
  WASHER: BiSolidWasher,
  DRYER: BiSolidDryer,
  DISHWASHER: BiSolidDish,
} as const;

type Props = {
  appliance: Appliance;
  upcomingBooking?: Booking | null;
  onChanged: () => void;
};

export function ApplianceCard({ appliance, upcomingBooking, onChanged }: Props) {
  const [busy, setBusy] = useState<ApplianceState | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setState = async (state: ApplianceState, confirmMessage?: string) => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    setError(null);
    setBusy(state);
    try {
      await api.post(`/appliance/${appliance.id}/event`, { state, source: "MANUAL" });
      onChanged();
    } catch (err) {
      setError(errMsg(err, "Couldn't update appliance state."));
    } finally {
      setBusy(null);
    }
  };

  const claim = async () => {
    setError(null);
    setClaiming(true);
    try {
      await api.post(`/appliance/${appliance.id}/claim`, {});
      onChanged();
    } catch (err) {
      setError(errMsg(err, "Couldn't claim this run."));
    } finally {
      setClaiming(false);
    }
  };

  // A sensor-started run nobody attached themselves to. Manual/NFC starts always
  // stamp a user, so this only shows for HA-driven washer/dryer runs.
  const isUnclaimed =
    appliance.state === "RUNNING" &&
    appliance.name !== "DISHWASHER" &&
    !appliance.runOwner &&
    appliance.lastEvent?.userId == null;

  const tone =
    appliance.state === "RUNNING"
      ? "amber"
      : appliance.state === "DONE"
        ? "emerald"
        : "slate";

  const isRunning = appliance.state === "RUNNING";
  const Icon = APPLIANCE_ICON[appliance.name];

  const pulseBorder = useTransform(appliancePulse, [0, 1], ["#e2e8f0", "#fcd34d"]);
  const pulseShadow = useTransform(
    appliancePulse,
    [0, 1],
    ["0 1px 3px rgba(0,0,0,0.05)", "0 0 0 4px rgba(251,191,36,0.12)"],
  );

  return (
    <motion.div
      className="flex flex-col gap-4 rounded-xl border bg-white p-5"
      style={
        isRunning
          ? { borderColor: pulseBorder, boxShadow: pulseShadow }
          : { borderColor: "#e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {/* Icon container — bounces on DONE */}
          <motion.div
            key={appliance.state}
            className="grid h-10 w-10 place-items-center rounded-lg bg-brand-50 text-brand-700"
            animate={appliance.state === "DONE" ? { scale: [1, 1.3, 1] } : {}}
            transition={{ duration: 0.35, ease: "easeOut" }}
          >
            {/* Icon itself — spins while RUNNING */}
            <motion.div
              animate={isRunning ? { rotate: 360 } : { rotate: 0 }}
              transition={
                isRunning
                  ? { duration: 3, repeat: Infinity, ease: "linear" }
                  : { duration: 0.5, ease: "easeOut" }
              }
            >
              <Icon className="h-6 w-6" />
            </motion.div>
          </motion.div>

          <div>
            <h3 className="text-base font-semibold text-slate-900">
              {APPLIANCE_LABEL[appliance.name]}
            </h3>
            {appliance.lastEvent ? (
              <p
                className="text-xs text-slate-500"
                title={formatDateTime(appliance.lastEvent.timestamp)}
              >
                Updated {formatRelativeTime(appliance.lastEvent.timestamp)}
              </p>
            ) : (
              <p className="text-xs text-slate-400">No activity yet</p>
            )}
          </div>
        </div>
        <Pill tone={tone}>{STATE_LABEL[appliance.state]}</Pill>
      </div>

      {appliance.state !== "IDLE" && appliance.name !== "DISHWASHER" && appliance.runOwner && (
        <div
          className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 ${
            isRunning ? "border-amber-100 bg-amber-50" : "border-emerald-100 bg-emerald-50"
          }`}
        >
          <Avatar name={appliance.runOwner.name ?? "?"} colour={appliance.runOwner.colour} size="xs" />
          <span className={`text-xs ${isRunning ? "text-amber-800" : "text-emerald-800"}`}>
            {appliance.runOwner.name}&rsquo;s load
          </span>
        </div>
      )}

      {isUnclaimed && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-rose-100 bg-rose-50 px-3 py-1.5">
          <span className="text-xs text-rose-800">Running &middot; nobody&rsquo;s claimed it</span>
          <Button
            variant="secondary"
            onClick={claim}
            loading={claiming}
            disabled={claiming}
            className="!px-3 !py-1 text-xs"
          >
            Claim
          </Button>
        </div>
      )}

      {appliance.state === "IDLE" && appliance.claim?.user && (
        <div className="flex items-center gap-2 rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-1.5">
          <Avatar name={appliance.claim.user.name ?? "?"} colour={appliance.claim.user.colour} size="xs" />
          <span className="text-xs text-indigo-800">
            Claimed by {appliance.claim.user.name ?? "someone"} &middot; waiting to start
          </span>
        </div>
      )}

      {upcomingBooking && (
        <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <Avatar
              name={upcomingBooking.user.name}
              colour={upcomingBooking.user.colour}
              size="xs"
            />
            <span className="font-medium text-slate-800">{upcomingBooking.user.name}</span>
            <span>·</span>
            <span>{formatDateTime(upcomingBooking.slotStart)}</span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {appliance.state === "IDLE" && (
          <Button
            onClick={() => setState("RUNNING")}
            loading={busy === "RUNNING"}
            disabled={busy !== null}
          >
            Start
          </Button>
        )}
        {appliance.state === "RUNNING" && (
          <Button
            variant="secondary"
            onClick={() => setState("DONE")}
            loading={busy === "DONE"}
            disabled={busy !== null}
          >
            Mark done
          </Button>
        )}
        {appliance.state === "DONE" && (
          <Button
            variant="secondary"
            onClick={() => setState("IDLE", "Reset this appliance back to idle?")}
            loading={busy === "IDLE"}
            disabled={busy !== null}
          >
            Reset to idle
          </Button>
        )}
      </div>

      {error && <p className="text-xs text-rose-600">{error}</p>}
    </motion.div>
  );
}
